import { Fragment, ReactElement, useEffect, useState } from "react";
import { IMillProductDetail } from "../../types";
import {
  CircularProgress,
  Paper,
  Table, TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel, Tooltip
} from "@mui/material";
import {
  formatDateTimeWithSeconds, formatTimeWithSeconds,
  getComparator, saveBlobAs,
  stableSort, toTons
} from "../../../../system/utils";
import { IColumn, Order } from "../../../../types";
import StyledTableRow from "../../../../components/styledTableRow";
import IconButton from "@mui/material/IconButton";
import TimelineIcon from "@mui/icons-material/Timeline";
import Detail from "../../../../components/dialogs/detail";
import ProductTrends from "./productTrends";
import TableContainer  from "../../../../components/tableContainer";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { getPalette } from "../../../../configuration/configuration";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

interface IProductDetailProps {
  products: IMillProductDetail[] | null;
  setSelectedItem: (item: IMillProductDetail) => void;
  selectedItem: IMillProductDetail | null;
}

interface IDetailDialog {
  open: boolean;
  item: IMillProductDetail | null;
}

const columns: IColumn[] = [
  {
    id: "actions",
    label: "",
    minWidth: 20
  },
  {
    id: "status",
    label: "",
    minWidth: 20
  },
  {
    id: "billet",
    label: "Billet",
    minWidth: 50,
  },
  {
    id: "weight",
    label: "Weight [lb]",
    minWidth: 120
  },
  {
    id: "compactingLength",
    label: "Length [mm]",
    minWidth: 130
  },
  {
    id: "compactingPressure",
    label: "Pressure []",
    minWidth: 120
  },
  {
    id: "pressing",
    label: "Pressing",
    minWidth: 80
  },
  // {
  //   id: "beginDatetime",
  //   label: "Begin Ring Dist.",
  //   minWidth: 180,
  //   format: value => formatDateTimeWithSeconds(value),
  // },
  // {
  //   id: "endDatetime",
  //   label: "End Ring Dist.",
  //   minWidth: 180,
  //   format: value => formatDateTimeWithSeconds(value),
  // }
];

const ProductDetail = ({ products, selectedItem, setSelectedItem }: IProductDetailProps): ReactElement => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof IMillProductDetail>("beginDatetime");
  const [openDetail, setOpenDetail] = useState<IDetailDialog>({ open: false, item: null });
  const [ tableColumns, setTableColumns ] = useState<IColumn[]>(columns);

  useEffect(() => {
    if (products && products.length > 0) {

      const newColumns = [
        ...columns,
        {
          id: "process@~>begin",
          subLabel: "Process",
          label: "Begin",
          minWidth: 80,
          format: (value: number) => formatDateTimeWithSeconds(value),
        },
        {
          id: "process@~>end",
          subLabel: "Process",
          label: "End",
          minWidth: 80,
          format: (value: number) => formatDateTimeWithSeconds(value),
        }];

      Object.values(products[0].stages ?? {}).sort((a, b) => a.sortOrder - b.sortOrder).forEach(s => {

        newColumns.push(
          {
            id: `stages@~>${s.code}@~>begin`,
            subLabel: s.name,
            label: "Begin",
            minWidth: 80,
            format: value => formatTimeWithSeconds(value),
          },
          {
            id: `stages@~>${s.code}@~>end`,
            subLabel: s.name,
            label: "End",
            minWidth: 80,
            format: value => formatTimeWithSeconds(value),
          });

      });

      setTableColumns(newColumns);
    }
  }, [products]);

  const createSortHandler = (property: keyof IMillProductDetail) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const  onShowCharts = (item: IMillProductDetail) => {
    setOpenDetail({ open: true, item });
  };

  const group = [ "Process", ...new Set(tableColumns.filter(f => f.id.startsWith("stages")).map(s => s.subLabel) as string[] ) ];

  const onExportData = () => {
    let fileContent = "";

    // fileContent += `FROM;${filter.local.selectedFrom.toFormat("MM-dd-yyyy")}\n`;
    // fileContent += `TO;${filter.local.selectedTo.toFormat("MM-dd-yyyy")}\n`;
    // fileContent += `Shift;${filter.local.shift}\n`;
    // fileContent += `Crew;${filter.local.crew}\n`;
    // fileContent += "\n";
    fileContent += "Strand;Status;Mill Heat;Heat Number;Steel Grade;Billet;Weight[US ton];Compacting Length[mm];Compacting Pressure;Pressing;Begin Datetime;End Datetime;\n";

    products?.forEach(row => {
      let status = "Pending";
      if (row.isCutUp) {
        status = "Cut up";
      } else if (row.isCobble) {
        status = "Cobble";
      } else if (row.weight) {
        status = "Processed";
      }

      let pressing = "---";
      if (row.pressing === 0) {
        pressing = "x1";
      } else if (row.pressing === 1) {
        pressing = "x2";
      }

      fileContent += `${row.machine.name};${status};${row.millHeat};${row.meltHeat};${row.grade};${row.billet};`;
      fileContent += `${row.weight ? toTons(row.weight).toFixed(2) : ""};${row.compactingLength ?? ""};`;
      fileContent += `${row.compactingPressure ?? ""};${pressing};`;
      fileContent += `${formatDateTimeWithSeconds(row.beginDatetime)};${formatDateTimeWithSeconds(row.endDatetime)};\n`;
    });

    const file = new Blob([ fileContent ], { type: "text/csv" });

    saveBlobAs(URL.createObjectURL(file), "mr-billets-details.csv");
  };


  return (
    <div>
      <IconButton color="primary" onClick={onExportData} disabled={!products}>
        <Tooltip title={"Export data"}>
          <FileDownloadIcon/>
        </Tooltip>
      </IconButton>
      <TableContainer>
        <Table stickyHeader={true}  size={"small"} >
          <TableHead>
            { group.length > 0 &&
              <TableRow>
                <TableCell className="table-header" colSpan={4}/>
                <TableCell className="table-header" colSpan={2} align={"center"}>Compacting</TableCell>
                {group.map((column, index) =>
                  <TableCell className="table-header" key={`group-${index}`} colSpan={2} align={"center"}>
                    {column}
                  </TableCell>
                )}
              </TableRow>
            }
            <TableRow>
              {tableColumns.map((column) => {

                if (column.id.startsWith("stages@~>")) {
                  return(
                    <TableCell className="table-header" key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>);
                }

                return (
                  <TableCell
                    className="table-header"
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                    sortDirection={orderBy === column.id ? order : false}>
                    {column.id !== "actions"
                      ? <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={createSortHandler(column.id as keyof IMillProductDetail)}
                      >
                        {column.label}
                        {orderBy === column.id
                          ? <span className="cell-visuallyHidden">
                            {order === "desc" ? "sorted descending" : "sorted ascending"}
                          </span>
                          : null}
                      </TableSortLabel>
                      : <Fragment/>}
                  </TableCell>);
              }
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {products && stableSort<IMillProductDetail>(products, getComparator(order, orderBy))
              .map((row: any, index: number) => {
                return (
                  <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`alarm-row-${index}`}
                    onClick={() => setSelectedItem(row)} selected={row === selectedItem}>
                    {tableColumns.map((column: IColumn) => {
                      if (column.id === "actions") {
                        return (
                          <TableCell key={column.id} align={column.align} className="table-cell">
                            <IconButton aria-label="edit" size={"small"}  onClick={() => onShowCharts(row as IMillProductDetail)}>
                              <TimelineIcon/>
                            </IconButton>
                          </TableCell>
                        );
                      }

                      if (column.id === "status") {
                        let status = 0;
                        if(row.status === 0){
                          status = row.weight ? 1 : 0;
                        }

                        return (
                          <TableCell key={column.id} align={column.align} className="table-cell">
                            {status === 0 &&
                            <Tooltip title="Pending">
                              <ErrorOutlineIcon style={{ color: getPalette().statusColor.info.background, verticalAlign: "center", marginLeft: "0.5em" }}/>
                            </Tooltip>}
                            {status === 1 &&
                          <Tooltip title="Processed">
                            <CheckCircleOutlineIcon style={{ color: getPalette().statusColor.good.background, verticalAlign: "center", marginLeft: "0.5em" }}/>
                          </Tooltip>
                            }
                            {status === 2 &&
                            <Tooltip title="Rejected">
                              <HighlightOffIcon style={{ color: getPalette().statusColor.error.background, verticalAlign: "center", marginLeft: "0.5em" }}/>
                            </Tooltip>
                            }
                          </TableCell>
                        );
                      }

                      if (column.id.startsWith("stages@~>")) {
                        const tokens = column.id.split("@~>");
                        if (tokens[2] === "begin") {
                          return (
                            <TableCell key={column.id} align={column.align} className="table-cell">
                              {column.format ? column.format(row.stages[tokens[1]].beginDatetime) : row.stages[tokens[1]].beginDatetime}
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell key={column.id} align={column.align} className="table-cell">
                            {column.format ? column.format(row.stages[tokens[1]].endDatetime) : row.stages[tokens[1]].endDatetime}
                          </TableCell>
                        );
                      }

                      if (column.id.startsWith("process@~>")) {
                        const tokens = column.id.split("@~>");
                        if (tokens[1] === "begin") {
                          return (
                            <TableCell key={column.id} align={column.align} className="table-cell">
                              {column.format ? column.format(row.beginDatetime) :row.beginDatetime}
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell key={column.id} align={column.align} className="table-cell">
                            {column.format ? column.format(row.endDatetime) : row.endDatetime}
                          </TableCell>
                        );
                      }

                      if (column.id === "pressing") {
                        let pressing = "---";
                        if (row.pressing === 0) {
                          pressing = "x1";
                        } else if (row.pressing === 1) {
                          pressing = "x2";
                        }

                        return (
                          <TableCell key={column.id} align={column.align} className="table-cell">
                            {pressing}
                          </TableCell>
                        );
                      }

                      const value = row[column.id];

                      return (
                        <TableCell key={column.id} align={column.align} className="table-cell">
                          {column.format && typeof value === "number" ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
        {!products &&
        <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2"  >
          <CircularProgress className=""/>
        </Paper>
        }
      </TableContainer>
      {openDetail.open && openDetail.item &&
      <Detail closed={() => setOpenDetail({ open: false, item: null })} fullWidth={true} maxWidth={"lg"}
        title={`Charts - Mill Order ${openDetail.item.millHeat} - ${openDetail.item.machine.name} - Billet ${openDetail.item.billet}`}>
        <ProductTrends productDetail={openDetail.item}/>
      </Detail>
      }
    </div>
  );
};

export default ProductDetail;
